/*
 *  This file hrone-tooltip.directive.ts is part of HROne. The intellectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2024
 *  (C) 2015-2024. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 */

import {
  ApplicationRef,
  ComponentRef,
  computed,
  createComponent,
  Directive,
  ElementRef,
  EnvironmentInjector,
  numberAttribute,
  TemplateRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { HroneTooltipComponent } from './hrone-tooltip.component';

@Directive({
  selector: '[hroneTooltip]',
  exportAs: 'hroneTooltip',
})
export class HroneTooltipDirective implements OnInit, OnDestroy {

  /** Tooltip text content — also accepts a TemplateRef (rich content) or null (suppresses the tooltip), for drop-in compatibility with PrimeNG pTooltip usages */
  readonly tooltip = input<string | TemplateRef<unknown> | null>('', { alias: 'hroneTooltip' });

  /** Preferred position. Auto-flips if the tooltip clips the viewport. */
  readonly tooltipPosition = input<'top' | 'bottom' | 'left' | 'right'>('top');

  /** Extra CSS class applied to the tooltip element */
  readonly tooltipStyleClass = input('');

  /** Append tooltip to body (recommended inside modals) or host parent */
  readonly appendTo = input<'body' | 'self'>('body');

  /** Milliseconds before the tooltip appears. Coerces a literal attribute (e.g. showDelay="1000") to a number. */
  readonly showDelay = input(0, { transform: numberAttribute });

  /** Milliseconds before the tooltip disappears. Coerces a literal attribute (e.g. hideDelay="500") to a number. */
  readonly hideDelay = input(0, { transform: numberAttribute });

  /**
   * Set true to suppress the tooltip entirely — matches PrimeNG's pTooltip
   * attribute name for drop-in migration.
   *
   * Deliberately NOT named `disabled`: a host element's native/component
   * `[disabled]` binding must keep disabling the control itself (and nothing
   * else) rather than being silently claimed by this directive as well —
   * Angular only lets one input per name receive a given property binding.
   * Use `[tooltipDisabled]` when the tooltip needs to be suppressed
   * independently of the control's disabled state.
   */
  readonly tooltipDisabled = input(false);

  /** z-index of the tooltip overlay */
  readonly tooltipZIndex = input(9999);

  /** Size variant — adds hrone-tooltip-sm/-md/-lg on the tooltip container */
  readonly tooltipSize = input<'small' | 'medium' | 'large'>('small');

  /** Rich content — takes precedence over the hroneTooltip string when provided */
  readonly hroneTooltipTemplate = input<TemplateRef<unknown> | null>(null);

  /**
   * Accepted for PrimeNG pTooltip drop-in compatibility only — no-op.
   * Rich content is handled automatically via TemplateRef detection on `tooltip`/`hroneTooltipTemplate`,
   * so there is nothing left for `escape` to control.
   */
  readonly escape = input(true);

  /**
   * Accepted for PrimeNG pTooltip drop-in compatibility (`[tooltipOptions]="{ showDelay, hideDelay }"`).
   * When set, its `showDelay`/`hideDelay` take precedence over the individual inputs.
   */
  readonly tooltipOptions = input<{ showDelay?: number; hideDelay?: number } | null>(null);

  // ── Derived/merged signals ──────────────────────────────────────────────────

  /** Normalized string content — `null` and TemplateRef values collapse to '' here; rich content is read via effectiveTemplate. */
  private readonly normalizedTooltip = computed(() => {
    const value = this.tooltip();
    return value instanceof TemplateRef ? '' : (value ?? '');
  });

  /** TemplateRef content — either passed via `hroneTooltip` directly, or the dedicated `hroneTooltipTemplate` input. */
  private readonly effectiveTemplate = computed(() => {
    const value = this.tooltip();
    return value instanceof TemplateRef ? value : this.hroneTooltipTemplate();
  });

  /** Effective show delay — `tooltipOptions.showDelay` wins over the standalone `showDelay` input when provided. */
  private readonly effectiveShowDelay = computed(() => this.tooltipOptions()?.showDelay ?? this.showDelay());

  /** Effective hide delay — `tooltipOptions.hideDelay` wins over the standalone `hideDelay` input when provided. */
  private readonly effectiveHideDelay = computed(() => this.tooltipOptions()?.hideDelay ?? this.hideDelay());

  // ── Private state ───────────────────────────────────────────────────────────
  private readonly SMALL_DEVICE_BREAKPOINT = 768;
  private readonly SMALL_DEVICE_DEFAULT_SHOW_DELAY = 500;
  private readonly TOUCH_GHOST_THRESHOLD_MS = 600;
  private componentRef: ComponentRef<HroneTooltipComponent> | null = null;
  private tooltipEl: HTMLElement | null = null;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly tooltipId = `hrone-tooltip-${Math.random().toString(36).slice(2, 9)}`;
  private readonly unlisteners: (() => void)[] = [];
  private lastTouchTime = 0;
  private detachObserver: IntersectionObserver | null = null;
  private windowScrollListener: (() => void) | null = null;
  private windowResizeListener: (() => void) | null = null;
  private documentTouchListener: ((e: TouchEvent) => void) | null = null;

  // ── DI ─────────────────────────────────────────────────────────────────────
  private readonly el = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);

  // ── Reactive sync into an already-rendered tooltip ─────────────────────────

  constructor() {
    // Push live content/style changes into the visible tooltip instance.
    effect(() => {
      const content = this.normalizedTooltip();
      const styleClass = this.tooltipStyleClass();
      const size = this.tooltipSize();
      const template = this.effectiveTemplate();
      if (!this.componentRef) return;
      this.componentRef.instance.content.set(content);
      this.componentRef.instance.styleClass.set(styleClass);
      this.componentRef.instance.size.set(size);
      this.componentRef.instance.template.set(template);
    });

    // Reposition the visible tooltip when the preferred position changes.
    effect(() => {
      const position = this.tooltipPosition();
      if (!this.componentRef) return;
      this.componentRef.instance.position.set(position);
      requestAnimationFrame(() => this.positionTooltip());
    });

    // Suppress the tooltip as soon as it becomes disabled.
    effect(() => {
      if (this.tooltipDisabled()) {
        this.destroyTooltip();
      }
    });
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.attachListeners();
  }

  ngOnDestroy(): void {
    this.clearShowTimer();
    this.clearHideTimer();
    this.unlisteners.forEach(fn => fn());
    this.destroyTooltip();
  }

  // ── Event attachment (always-on: these are what trigger show/hide) ─────────

  private attachListeners(): void {
    const host = this.el.nativeElement;

    // Desktop — hover (guard against ghost mouse events fired after touch)
    this.unlisteners.push(
      this.renderer.listen(host, 'mouseenter', () => {
        if (Date.now() - this.lastTouchTime < this.TOUCH_GHOST_THRESHOLD_MS) return;
        this.scheduleShow();
      }),
      this.renderer.listen(host, 'mouseleave', () => {
        if (Date.now() - this.lastTouchTime < this.TOUCH_GHOST_THRESHOLD_MS) return;
        this.scheduleHide();
      }),
    );

    // Desktop — focus (always active for accessibility). Guarded to
    // :focus-visible so it only fires for genuine keyboard focus — e.g. a
    // PrimeNG/Material select restores focus to its trigger programmatically
    // after a mouse-driven selection closes the panel, which would otherwise
    // pop the tooltip back open right after the user picked a value.
    this.unlisteners.push(
      this.renderer.listen(host, 'focus', () => {
        if (host.matches?.(':focus-visible')) this.scheduleShow();
      }),
      this.renderer.listen(host, 'blur', () => this.scheduleHide()),
    );

    // Selection (e.g. picking a dropdown option) can remove/hide the host
    // instantly without ever firing mouseleave — hide immediately on click
    // so the tooltip doesn't stay stranded on screen.
    this.unlisteners.push(
      this.renderer.listen(host, 'click', () => this.hide()),
    );

    // Mobile — touch on host (trigger). Native addEventListener so it can be
    // marked passive — Renderer2.listen() always binds non-passive, and this
    // handler never calls preventDefault(), so passive avoids it being flagged
    // as a scroll-blocking listener. Dismiss is handled by document touchstart
    // (tap outside) or scroll — see bindVisibleGuards().
    const hostTouchStart = (e: TouchEvent) => {
      e.stopPropagation();
      this.lastTouchTime = Date.now();
      this.scheduleShow();
    };
    host.addEventListener('touchstart', hostTouchStart, { passive: true });
    this.unlisteners.push(() => host.removeEventListener('touchstart', hostTouchStart));

    this.unlisteners.push(
      this.renderer.listen(host, 'touchend', () => {
        // Quick tap: timer hasn't fired yet — cancel it so tooltip never shows
        // Long press: tooltip already visible — leave it, dismissed by tap-outside or scroll
        if (!this.componentRef) {
          this.clearShowTimer();
        }
      }),
    );
  }

  // ── Visible-only guards ─────────────────────────────────────────────────────
  // Window scroll/resize, document tap-outside, and detach detection all exist
  // purely to auto-hide an *already-visible* tooltip — bound only while one is
  // actually shown (from createTooltip()/destroyTooltip()) rather than for the
  // whole lifetime of every directive instance. With this directive on ~3,800
  // elements app-wide, keeping these permanently live per instance would mean
  // hundreds of simultaneous window/document listeners and IntersectionObservers
  // on a single busy page (e.g. a large table), the vast majority never hovered.
  //
  // All three use native addEventListener with { passive: true } rather than
  // Renderer2.listen() (which always binds non-passive) — none of them call
  // preventDefault(), and non-passive scroll/touchstart listeners are a known
  // scroll-jank source (Chrome DevTools flags this as a "violation").
  private bindVisibleGuards(): void {
    const host = this.el.nativeElement;

    // A hovered element removed from the DOM (e.g. a dropdown/select option
    // right after it's picked) never fires mouseleave — there's no "the mouse
    // is no longer over it" event for an element that's simply gone. Without
    // this, the tooltip is never told to hide, and the next positionTooltip()
    // call renders it collapsed to (0,0) — getBoundingClientRect() on a
    // disconnected element returns an all-zero rect. IntersectionObserver
    // reliably reports isIntersecting: false the instant its target
    // disconnects (it also fires for "scrolled out of the viewport", a
    // harmless bonus).
    this.detachObserver = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) this.hide();
    });
    this.detachObserver.observe(host);

    this.windowScrollListener = () => this.hide();
    this.windowResizeListener = () => this.hide();
    window.addEventListener('scroll', this.windowScrollListener, { passive: true });
    window.addEventListener('resize', this.windowResizeListener, { passive: true });

    // Mobile — tap outside to dismiss
    this.documentTouchListener = (e: TouchEvent) => {
      if (!host.contains(e.target as Node)) this.hide();
    };
    document.addEventListener('touchstart', this.documentTouchListener, { passive: true });
  }

  private unbindVisibleGuards(): void {
    this.detachObserver?.disconnect();
    this.detachObserver = null;

    if (this.windowScrollListener) {
      window.removeEventListener('scroll', this.windowScrollListener);
      this.windowScrollListener = null;
    }
    if (this.windowResizeListener) {
      window.removeEventListener('resize', this.windowResizeListener);
      this.windowResizeListener = null;
    }
    if (this.documentTouchListener) {
      document.removeEventListener('touchstart', this.documentTouchListener);
      this.documentTouchListener = null;
    }
  }

  // ── Show / hide scheduling ──────────────────────────────────────────────────

  private isSmallDevice(): boolean {
    return window.innerWidth <= this.SMALL_DEVICE_BREAKPOINT;
  }

  private scheduleShow(): void {
    this.clearShowTimer();
    this.clearHideTimer();
    // Consumer-provided delay takes priority.
    // On small devices with no explicit delay, apply default 500ms so the
    // tooltip doesn't fire on every accidental touch scroll.
    const delay = this.effectiveShowDelay() > 0
      ? this.effectiveShowDelay()
      : this.isSmallDevice() ? this.SMALL_DEVICE_DEFAULT_SHOW_DELAY : 0;

    if (delay > 0) {
      this.showTimer = setTimeout(() => this.show(), delay);
    } else {
      this.show();
    }
  }

  private scheduleHide(): void {
    this.clearShowTimer();
    if (this.effectiveHideDelay() > 0) {
      this.hideTimer = setTimeout(() => this.hide(), this.effectiveHideDelay());
    } else {
      this.hide();
    }
  }

  // ── Core show / hide ────────────────────────────────────────────────────────
  // Public so consumers can grab a template reference (`#t="hroneTooltip"`) and
  // trigger the tooltip programmatically — matches PrimeNG pTooltip / Angular
  // Material matTooltip's public show()/hide() API for drop-in compatibility.

  show(): void {
    const hasTemplate = !!this.effectiveTemplate();
    if (this.tooltipDisabled() || (!hasTemplate && !this.normalizedTooltip()?.trim())) return;
    if (this.componentRef) return; // already visible

    this.createTooltip();

    // Wait one frame so the tooltip has rendered dimensions before positioning
    requestAnimationFrame(() => {
      this.positionTooltip();
      if (this.componentRef) {
        this.componentRef.instance.visible.set(true);
        this.renderer.setAttribute(this.el.nativeElement, 'aria-describedby', this.tooltipId);
      }
    });
  }

  hide(): void {
    this.clearShowTimer();
    this.renderer.removeAttribute(this.el.nativeElement, 'aria-describedby');
    this.destroyTooltip();
  }

  // ── Component creation / destruction ────────────────────────────────────────

  private createTooltip(): void {
    this.componentRef = createComponent(HroneTooltipComponent, {
      environmentInjector: this.injector,
    });

    const inst = this.componentRef.instance;
    inst.content.set(this.normalizedTooltip());
    inst.position.set(this.tooltipPosition());
    inst.styleClass.set(this.tooltipStyleClass());
    inst.zIndex.set(this.tooltipZIndex());
    inst.tooltipId.set(this.tooltipId);
    inst.size.set(this.tooltipSize());
    inst.template.set(this.effectiveTemplate());

    this.appRef.attachView(this.componentRef.hostView);
    // Force the initial render synchronously — a freshly attached OnPush view
    // is not guaranteed to have been checked yet, so the template (and the
    // .hrone-tooltip element we're about to pull out of it) may not exist.
    this.componentRef.changeDetectorRef.detectChanges();

    const tooltipEl = this.componentRef.location.nativeElement.querySelector('.hrone-tooltip') as HTMLElement;
    tooltipEl.style.top = '0';
    tooltipEl.style.left = '0';
    this.tooltipEl = tooltipEl;

    // Only the inner .hrone-tooltip element gets appended — never the
    // <app-hrone-tooltip> host wrapper.
    const target = this.appendTo() === 'body'
      ? document.body
      : (this.el.nativeElement.parentElement ?? document.body);

    target.appendChild(tooltipEl);
    this.bindVisibleGuards();
  }

  private destroyTooltip(): void {
    if (this.componentRef) {
      // Angular's view-destroy only removes the (never-inserted) host wrapper —
      // the .hrone-tooltip element was manually moved out of it, so it must be
      // removed from the DOM explicitly.
      this.tooltipEl?.remove();
      this.tooltipEl = null;
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
      this.unbindVisibleGuards();
    }
  }

  // ── Positioning ─────────────────────────────────────────────────────────────

  private positionTooltip(): void {
    if (!this.componentRef || !this.tooltipEl) return;

    // Belt-and-suspenders: if the host was removed from the DOM between show()
    // scheduling this (via requestAnimationFrame) and now, its rect is all
    // zeroes — hide instead of rendering the tooltip pinned at (0,0). Normally
    // the IntersectionObserver in bindVisibleGuards() catches this first.
    if (!this.el.nativeElement.isConnected) {
      this.hide();
      return;
    }

    const hostRect = this.el.nativeElement.getBoundingClientRect();
    const tooltipEl = this.tooltipEl;

    const tooltipRect = tooltipEl.getBoundingClientRect();
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const arrowGap = 4; // arrow (6px) + gap (2px)

    // Compute raw coordinates for the requested position
    const computeCoords = (pos: string): { top: number; left: number } => {
      const cx = hostRect.left + scrollX + hostRect.width / 2;
      const cy = hostRect.top + scrollY + hostRect.height / 2;
      switch (pos) {
        case 'top':
          return {
            top:  hostRect.top  + scrollY - tooltipRect.height - arrowGap,
            left: cx - tooltipRect.width / 2,
          };
        case 'bottom':
          return {
            top:  hostRect.bottom + scrollY + arrowGap,
            left: cx - tooltipRect.width / 2,
          };
        case 'left':
          return {
            top:  cy - tooltipRect.height / 2,
            left: hostRect.left + scrollX - tooltipRect.width - arrowGap,
          };
        case 'right':
          return {
            top:  cy - tooltipRect.height / 2,
            left: hostRect.right + scrollX + arrowGap,
          };
        default:
          return { top: 0, left: 0 };
      }
    };

    // Auto-flip: switch to opposite side if position clips the viewport
    const willClip = (pos: string, top: number, left: number): boolean => {
      switch (pos) {
        case 'top':    return top < scrollY;
        case 'bottom': return top + tooltipRect.height > scrollY + vh;
        case 'left':   return left < scrollX;
        case 'right':  return left + tooltipRect.width > scrollX + vw;
        default:       return false;
      }
    };

    const opposites: Record<'top' | 'bottom' | 'left' | 'right', 'top' | 'bottom' | 'left' | 'right'> = {
      top: 'bottom', bottom: 'top', left: 'right', right: 'left',
    };

    let activePos = this.tooltipPosition();
    let { top, left } = computeCoords(activePos);

    if (willClip(activePos, top, left)) {
      const flipped = opposites[activePos];
      const flippedCoords = computeCoords(flipped);
      if (!willClip(flipped, flippedCoords.top, flippedCoords.left)) {
        activePos = flipped;
        top = flippedCoords.top;
        left = flippedCoords.left;
      }
    }

    // Update arrow direction if we flipped
    if (activePos !== this.componentRef.instance.position()) {
      this.componentRef.instance.position.set(activePos);
    }

    // Coordinates before viewport clamping — where the box would sit if
    // perfectly centered on the host. Used below to keep the arrow pointing
    // at the host even after clamping shifts the box.
    const idealLeft = left;
    const idealTop = top;

    // Clamp horizontally/vertically so tooltip never renders off-screen
    left = Math.max(scrollX + 4, Math.min(left, scrollX + vw - tooltipRect.width - 4));
    top  = Math.max(scrollY + 4, Math.min(top,  scrollY + vh - tooltipRect.height - 4));

    // Clamping can slide the box away from the host's center — re-center the
    // arrow (not the box) on the host so it still points at the trigger
    // element, capped so it can't slide past the box's rounded corners.
    const arrowEdgeMargin = 12;
    let arrowOffset = 0;
    if (activePos === 'top' || activePos === 'bottom') {
      const maxOffset = Math.max(0, tooltipRect.width / 2 - arrowEdgeMargin);
      arrowOffset = Math.max(-maxOffset, Math.min(idealLeft - left, maxOffset));
    } else {
      const maxOffset = Math.max(0, tooltipRect.height / 2 - arrowEdgeMargin);
      arrowOffset = Math.max(-maxOffset, Math.min(idealTop - top, maxOffset));
    }
    this.componentRef.instance.arrowOffset.set(arrowOffset);

    tooltipEl.style.top  = `${top}px`;
    tooltipEl.style.left = `${left}px`;
  }

  // ── Timer helpers ───────────────────────────────────────────────────────────

  private clearShowTimer(): void {
    if (this.showTimer !== null) { clearTimeout(this.showTimer); this.showTimer = null; }
  }

  private clearHideTimer(): void {
    if (this.hideTimer !== null) { clearTimeout(this.hideTimer); this.hideTimer = null; }
  }
}
